/**
 * WorkflowAdapter Service
 *
 * Purpose: Decouple UI events from API calls, providing a clean interface for workflow operations.
 * This adapter ensures that UI components never directly import backend services.
 *
 * Responsibilities:
 * - Transform UI form data → RequirementSubmission (backend format) [DELEGATES TO mapping-unit]
 * - Call apiClient methods with proper error boundaries
 * - Cache recent requests to avoid redundant API calls [USES mapping-unit cache policy]
 * - Handle request debouncing for UPDATE scenarios
 * - Provide loading states separate from UI component states
 *
 * Modularity Principle: This is the ONLY service that communicates with the backend API
 * for recommendation workflows. All UI components must go through this adapter.
 *
 * Architecture: Delegates business logic to mapping-unit services
 */

import { CachePolicy } from '@mapping-unit/config/cachePolicy';
import { RequirementsTransformer } from '@mapping-unit/services/requirementsTransformer';
import type { RequirementFormData, RequirementSubmission } from '@mapping-unit/types/api';
import { apiClient } from './apiClient';

// UI Form Data Types (what the UI understands)
export interface RequirementsFormData {
    industry: string;
    businessProcesses: string[];
    companySize: string;
    specificNeeds: string[];
}

// Result Types
export interface ModuleRecommendation {
    name: string;
    technicalName: string;
    description: string;
    category: string;
    dependencies?: string[];
    securityRating: string;
    reasoning: string;
}

export interface RecommendationResult {
    recommendedModules: ModuleRecommendation[];
    reasoning: string;
    dependencies: string[];
    resourceImpact?: {
        estimatedCpu: number;
        estimatedRam: number;
        estimatedStorage: number;
    };
}

export interface ValidationResult {
    isValid: boolean;
    conflicts: string[];
    missingDependencies: string[];
    resourceWarnings: string[];
    securityIssues: string[];
}

// Cache for storing recent requests
interface CacheEntry {
    key: string;
    result: RecommendationResult;
    timestamp: number;
}

class WorkflowAdapter {
    private cache: Map<string, CacheEntry> = new Map();
    private readonly CACHE_TTL = CachePolicy.RECOMMENDATION_TTL_MS; // Use mapping-unit policy
    private pendingRequests: Map<string, Promise<RecommendationResult>> = new Map();
    private abortControllers: Map<string, AbortController> = new Map();

    /**
     * Transform UI form data to backend API format
     * DELEGATES to mapping-unit RequirementsTransformer service
     */
    private transformFormDataToSubmission(
        formData: RequirementsFormData
    ): RequirementSubmission {
        // Convert UI form data to mapping-unit format
        const mappingUnitFormat: RequirementFormData = {
            odooVersion: "18.0",
            businessDescription: `${formData.industry} business with ${formData.companySize} size`,
            expectedUsers: formData.companySize, // String value, mapping-unit will parse
            industry: formData.industry,
            specificNeeds: formData.specificNeeds,
            securityLevel: 'standard',
        };

        // Delegate to mapping-unit transformer
        return RequirementsTransformer.toApiFormat(mappingUnitFormat);
    }

    /**
     * Generate cache key from form data
     * DELEGATES to mapping-unit RequirementsTransformer service
     */
    private generateCacheKey(formData: RequirementsFormData): string {
        const mappingUnitFormat: RequirementFormData = {
            odooVersion: "18.0",
            businessDescription: `${formData.industry} business`,
            expectedUsers: formData.companySize,
            industry: formData.industry,
            specificNeeds: formData.specificNeeds,
            securityLevel: 'standard',
        };

        // Delegate to mapping-unit cache key generator
        return RequirementsTransformer.generateCacheKey(mappingUnitFormat);
    }

    /**
     * Get cached result if available and not expired
     */
    getCachedResult(formData: RequirementsFormData): RecommendationResult | null {
        const cacheKey = this.generateCacheKey(formData);
        const entry = this.cache.get(cacheKey);

        if (!entry) return null;

        const age = Date.now() - entry.timestamp;
        if (age > this.CACHE_TTL) {
            this.cache.delete(cacheKey);
            return null;
        }

        return entry.result;
    }

    /**
     * Store result in cache
     * Uses mapping-unit MAX_CACHE_SIZE policy
     */
    private setCachedResult(
        formData: RequirementsFormData,
        result: RecommendationResult
    ): void {
        const cacheKey = this.generateCacheKey(formData);

        // Enforce max cache size from mapping-unit policy
        if (this.cache.size >= CachePolicy.MAX_CACHE_SIZE) {
            // Remove oldest entry
            const firstKey = this.cache.keys().next().value;
            if (firstKey) {
                this.cache.delete(firstKey);
            }
        }

        this.cache.set(cacheKey, {
            key: cacheKey,
            result,
            timestamp: Date.now(),
        });
    }

    /**
     * Clear all cached results
     */
    clearCache(): void {
        this.cache.clear();
    }

    /**
     * Cancel all pending requests
     * Used when component unmounts or user navigates away
     */
    cancelPendingRequest(): void {
        for (const [key, controller] of this.abortControllers) {
            controller.abort();
            this.abortControllers.delete(key);
            this.pendingRequests.delete(key);
        }
    }

    /**
     * Submit requirements and get recommendations
     * This is the primary method UI components should call
     */
    async submitRequirements(
        formData: RequirementsFormData
    ): Promise<RecommendationResult> {
        // Check cache first
        const cachedResult = this.getCachedResult(formData);
        if (cachedResult) {
            console.log('WorkflowAdapter: Returning cached result');
            return cachedResult;
        }

        // Check for pending request with same data (prevent duplicate requests)
        const cacheKey = this.generateCacheKey(formData);
        const pendingRequest = this.pendingRequests.get(cacheKey);
        if (pendingRequest) {
            console.log('WorkflowAdapter: Returning pending request');
            return pendingRequest;
        }

        // Transform UI data to API format
        const submission = this.transformFormDataToSubmission(formData);

        // Create abort controller for this request
        const abortController = new AbortController();
        this.abortControllers.set(cacheKey, abortController);

        // Create request promise
        const requestPromise = (async () => {
            try {
                // Check if already aborted
                if (abortController.signal.aborted) {
                    throw new Error('Request aborted');
                }

                console.log('WorkflowAdapter: Submitting requirements to API', submission);

                // Call API through apiClient
                const response = await apiClient.createRecommendation(submission);

                // Check if aborted during request
                if (abortController.signal.aborted) {
                    throw new Error('Request aborted');
                }

                // API client returns an AxiosResponse; the API envelope is on
                // response.data. Check the data.success flag accordingly.
                if (!response.data || !response.data.success) {
                    throw new Error(response.data?.error || 'Failed to get recommendations');
                }

                // Extract the actual result
                const result: RecommendationResult = {
                    recommendedModules: response.data.recommendations || [],
                    reasoning: response.data.reasoning || 'No reasoning provided',
                    dependencies: [],
                    resourceImpact: response.data.resourceImpact,
                };

                // Cache the result
                this.setCachedResult(formData, result);

                return result;
            } catch (error) {
                console.error('WorkflowAdapter: Error submitting requirements', error);
                throw error;
            } finally {
                // Clean up pending request and abort controller
                this.pendingRequests.delete(cacheKey);
                this.abortControllers.delete(cacheKey);
            }
        })();

        // Store pending request
        this.pendingRequests.set(cacheKey, requestPromise);

        return requestPromise;
    }

    /**
     * Update requirements with modified form data
     * Used when user edits their selections in the results view
     */
    async updateRequirements(
        sessionId: string | null,
        formData: RequirementsFormData
    ): Promise<RecommendationResult> {
        // For updates, we treat them as new submissions
        // The sessionId is kept for potential future session management
        console.log('WorkflowAdapter: Updating requirements', { sessionId });

        // Clear cache to force fresh data
        const cacheKey = this.generateCacheKey(formData);
        this.cache.delete(cacheKey);

        // Submit as new request
        return this.submitRequirements(formData);
    }

    /**
     * Validate module selection
     * Called before accepting recommendations
     */
    async validateModules(
        sessionId: string,
        selectedModules: string[]
    ): Promise<ValidationResult> {
        try {
            console.log('WorkflowAdapter: Validating modules', { sessionId, selectedModules });

            const response = await apiClient.validateModules(sessionId, selectedModules);

            if (!response.data || !response.data.success) {
                throw new Error(response.data?.error || 'Validation failed');
            }

            return response.data;
        } catch (error) {
            console.error('WorkflowAdapter: Error validating modules', error);
            throw error;
        }
    }

    /**
     * Prepare installation configuration
     * This is the discrete function that generates the build configuration
     * 
     * @param sessionId - User session identifier
     * @param selectedModules - Technical names of selected modules
     * @param requirements - User requirements
     * @returns Installation configuration ready for display and execution
     */
    async prepareInstallation(
        sessionId: string,
        selectedModules: string[],
        requirements: RequirementsFormData
    ): Promise<any> {
        try {
            console.log('WorkflowAdapter: Preparing installation', { sessionId, selectedModules });

            // Transform UI requirements to backend format
            const submission = this.transformFormDataToSubmission(requirements);

            // Call backend orchestration endpoint via installation service
            const { installationOrchestrationService } = await import('./InstallationOrchestrationService');

            const configuration = await installationOrchestrationService.generateInstallationPlan(
                sessionId,
                selectedModules,
                submission
            );

            console.log('WorkflowAdapter: Installation configuration prepared', configuration);

            return configuration;
        } catch (error) {
            console.error('WorkflowAdapter: Error preparing installation', error);
            throw error;
        }
    }

    /**
     * Execute installation workflow
     * This triggers the deterministic installation process
     * 
     * @param configuration - Installation configuration from prepareInstallation
     * @returns Installation result with tracking information
     */
    async executeInstallation(configuration: any): Promise<any> {
        try {
            console.log('WorkflowAdapter: Executing installation', configuration.sessionId);

            const { installationOrchestrationService } = await import('./InstallationOrchestrationService');

            const result = await installationOrchestrationService.executeInstallation(configuration);

            console.log('WorkflowAdapter: Installation executed', result);

            return result;
        } catch (error) {
            console.error('WorkflowAdapter: Error executing installation', error);
            throw error;
        }
    }

    /**
     * Check installation status
     * Used for progress tracking during installation
     * 
     * @param installationId - Installation identifier
     * @returns Current installation status
     */
    async checkInstallationStatus(installationId: string): Promise<any> {
        try {
            const { installationOrchestrationService } = await import('./InstallationOrchestrationService');

            return await installationOrchestrationService.checkInstallationStatus(installationId);
        } catch (error) {
            console.error('WorkflowAdapter: Error checking installation status', error);
            throw error;
        }
    }
}

// Export singleton instance
export const workflowAdapter = new WorkflowAdapter();
